import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import config from '../../app/config';
import { color } from '../../app/theme';
import { MAStatusType } from '../../app/types';
import { setData } from '../../features/mainSlice';
import DatePicker from '../shared/DatePicker';
import TimePicker from '../shared/TimePicker';

const useStyles = makeStyles(
  theme => ({
    root: {
      marginBottom: 36,
    },
    title: {
      fontStyle: 'normal',
      fontWeight: 500,
      fontSize: 15,
      lineHeight: '15px',
      color: color.$3,
      marginBottom: 8,
    },
    subtitle: {
      fontSize: '13px',
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: 'normal',
      letterSpacing: 'normal',
      color: color.$13,
      marginBottom: 12,
    },
    timeSetting: {
      '& > div': {
        display: 'inline-block',
      },
      '& > div:last-child': {
        [theme.breakpoints.down('md')]: {
          display: 'block',
          marginTop: 8,
        },
      },
    },
    date: {
      width: 180,
      marginRight: 8,
    },
    time: {
      width: 100,
    },
    caterpillar: {
      fontSize: 13,
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: 1,
      letterSpacing: 'normal',
      color: color.$3,
      margin: '0 8px',
    },
  }),
  {
    name: 'Time',
  },
);

const Time = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const {
    startTime: tripStartDate,
    endTime: tripEndDate,
    status,
  } = useSelector(state => state.ma?.data);
  const { isActive, isSettingError } = useSelector(store => store.ma);
  const [startDate, setStartDate] = useState(moment().format('YYYY-MM-DD'));
  const [startTime, setStartTime] = useState(moment().hour(0).minute(0));
  const [endDate, setEndDate] = useState(
    moment().add(config.DEFAULT_TRIP_RANGE, 'days').format('YYYY-MM-DD'),
  );
  const [endTime, setEndTime] = useState(moment().hour(23).minute(59));

  const onChange = (date, type) => {
    if (type === 'start') {
      dispatch(
        setData({
          startTime: date,
        }),
      );
    }

    if (type === 'end') {
      dispatch(
        setData({
          endTime: date,
        }),
      );
    }
  };

  useEffect(() => {
    setStartDate(
      tripStartDate
        ? moment(tripStartDate).format('YYYY-MM-DD')
        : moment().format('YYYY-MM-DD'),
    );
    setStartTime(
      tripStartDate ? moment(tripStartDate) : moment().hour(0).minute(0),
    );
    setEndDate(
      tripEndDate
        ? moment(tripEndDate).format('YYYY-MM-DD')
        : moment().add(config.DEFAULT_TRIP_RANGE, 'days').format('YYYY-MM-DD'),
    );
    setEndTime(
      tripEndDate ? moment(tripEndDate) : moment().hour(23).minute(59),
    );
  }, [tripStartDate, tripEndDate]);

  useEffect(() => {
    if (status === MAStatusType.EDIT) {
      if (moment().isAfter(moment(tripEndDate))) {
        dispatch(
          setData({
            startTime: moment().format('YYYY-MM-DD'),
          }),
        );
      }
      if (moment().isAfter(moment(tripEndDate))) {
        dispatch(
          setData({
            endTime: moment()
              .add(config.DEFAULT_TRIP_RANGE, 'days')
              .format('YYYY-MM-DD'),
          }),
        );
      }
    }
  }, [status]);

  return (
    <div className={classes.root}>
      <div className={classes.title}>{t('setting.time')}</div>
      <div className={classes.subtitle}>{t('setting.time-subtitle')}</div>
      <div className={classes.timeSetting}>
        <div>
          <div style={{ display: 'flex' }}>
            <DatePicker
              className={classes.date}
              selectedDate={startDate}
              maxDate={endDate}
              minDate={moment()}
              onChange={value => {
                const date = `${moment(value).format('YYYY-MM-DD')} ${moment(
                  startTime,
                ).format('HH:mm')}`;

                if (moment(date).isBefore(moment(), 'minutes')) {
                  setStartDate(moment());
                  onChange(moment(), 'start');
                } else if (moment(date).isAfter(tripEndDate, 'minutes')) {
                  setStartDate(endTime);
                  onChange(tripEndDate, 'start');
                } else {
                  setStartDate(value);
                  onChange(date, 'start');
                }
              }}
              placeholder={t('date-placeholder')}
              disabled={isActive}
            />
            <TimePicker
              className={classes.time}
              selectedTime={startTime}
              isError={
                isSettingError &&
                moment(tripStartDate).isBefore(moment(), 'minutes')
              }
              onChange={value => {
                const date = `${moment(startDate).format(
                  'YYYY-MM-DD',
                )} ${moment(value).format('HH:mm')}`;

                if (moment(date).isBefore(moment(), 'minutes')) {
                  setStartTime(moment());
                  onChange(moment(), 'start');
                } else if (moment(date).isAfter(tripEndDate, 'minutes')) {
                  setStartTime(endTime);
                  onChange(tripEndDate, 'end');
                } else {
                  setStartTime(value);
                  onChange(date, 'start');
                }
              }}
              placeholder={t('time-placeholder')}
              disabled={isActive}
            />
          </div>
        </div>
        <div className={classes.caterpillar}>~</div>
        <div>
          <div style={{ display: 'flex' }}>
            <DatePicker
              className={classes.date}
              selectedDate={endDate}
              minDate={startDate || moment()}
              onChange={value => {
                const date = `${moment(value).format('YYYY-MM-DD')} ${moment(
                  endTime,
                ).format('HH:mm')}`;

                if (moment(date).isBefore(tripStartDate, 'minutes')) {
                  setEndTime(startTime);
                  onChange(tripStartDate, 'end');
                } else {
                  setEndDate(value);
                  onChange(value, 'end');
                }
              }}
              placeholder={t('date-placeholder')}
              disabled={isActive}
            />
            <TimePicker
              className={classes.time}
              selectedTime={endTime}
              onChange={value => {
                const date = `${moment(endDate).format('YYYY-MM-DD')} ${moment(
                  value,
                ).format('HH:mm')}`;

                if (moment(date).isBefore(tripStartDate, 'minutes')) {
                  setEndTime(startTime);
                  onChange(tripStartDate, 'end');
                } else {
                  setEndTime(value);
                  onChange(date, 'end');
                }
              }}
              placeholder={t('time-placeholder')}
              disabled={isActive}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Time;
